/**
 * 上下布局的展示统计数值。
 */
import { useEffect, useRef, useState } from "react";
import { handleHttp } from "@/components/Menu/ProCard/utils/valueConfig";
import { DEFAULT_DATA } from "@/config";
import "./index.less";

export interface StatisticProps {
  title?: string;
  value?: string;
  valueStyle?: object;
  valueConfig?: any;
  storeData?: any;
  updateKey?: number | undefined;
  /**
   * 轮询间隔（秒），大于 0 时启用定时刷新
   */
  needInterval?: number;
  /**
   * 是否自动格式化数字为 K/M/B/T
   * @default false
   */
  formatNumber?: boolean;
  /**
   * 格式化时保留的小数位数
   * @default 2
   */
  precision?: number;
  /**
   * 自定义单位数组，从千位开始
   * @default ['K', 'M', 'B', 'T']
   */
  units?: string[];
}

const StatisticClassPrefix = "iot-component-statistic";

/**
 * 格式化数字为 K/M/B/T 格式
 * @param num 要格式化的数字
 * @param precision 小数位数
 * @param units 单位数组
 * @returns 格式化后的字符串
 */
const formatNumberToUnit = (
  num: string | number,
  precision: number = 2,
  units: string[] = ["K", "M", "B", "T"]
): string => {
  const numValue = typeof num === "string" ? parseFloat(num) : num;

  if (isNaN(numValue)) {
    return String(num);
  }

  const absNum = Math.abs(numValue);
  const sign = numValue < 0 ? "-" : "";

  if (absNum < 1000) {
    return String(num);
  }

  const tier = Math.floor(Math.log10(absNum) / 3);
  const unitIndex = tier - 1;

  if (unitIndex >= units.length) {
    const divisor = Math.pow(1000, units.length);
    const scaled = numValue / divisor;
    return `${sign}${scaled.toFixed(precision)}${units[units.length - 1]}`;
  }

  const divisor = Math.pow(1000, tier);
  const scaled = numValue / divisor;

  return `${sign}${Math.abs(scaled).toFixed(precision)}${units[unitIndex]}`;
};

const Statistic = (props: StatisticProps) => {
  const {
    title,
    value = DEFAULT_DATA,
    valueStyle = {},
    valueConfig = null,
    storeData = {},
    updateKey,
    needInterval,
    formatNumber = false,
    precision = 2,
    units = ["K", "M", "B", "T"],
  } = props;
  const [result, setResult] = useState(value);
  const resultRef = useRef(result);
  resultRef.current = result;

  const updateData = () => {
    if (valueConfig) {
      if (
        valueConfig.type === "http" &&
        (resultRef.current === DEFAULT_DATA || updateKey)
      ) {
        handleHttp(valueConfig, (resultIn: any) => {
          setResult(resultIn);
        });
      } else if (
        valueConfig.type === "store" &&
        !["", DEFAULT_DATA, undefined].includes(
          storeData[valueConfig.dataKey]
        ) &&
        (resultRef.current === DEFAULT_DATA || updateKey)
      ) {
        setResult(storeData[valueConfig.dataKey]);
      }
    }
  };

  const pollData = () => {
    if (valueConfig?.type === "http") {
      handleHttp(valueConfig, (resultIn: any) => {
        setResult(resultIn);
      });
    }
  };

  useEffect(() => {
    updateData();
  }, [updateKey]);

  useEffect(() => {
    if (!needInterval || needInterval <= 0 || valueConfig?.type !== "http") {
      return;
    }
    const timer = setInterval(pollData, needInterval * 1000);
    return () => clearInterval(timer);
  }, [needInterval, valueConfig]);

  const displayValue = valueConfig ? result : value;
  const formattedValue = formatNumber
    ? formatNumberToUnit(displayValue, precision, units)
    : displayValue;

  return (
    <div className={`${StatisticClassPrefix}`}>
      <div
        className={`${StatisticClassPrefix}-content`}
        style={valueStyle}
        title={displayValue}
      >
        {formattedValue}
      </div>
      <div className={`${StatisticClassPrefix}-title`}>{title}</div>
    </div>
  );
};

export default Statistic;
